import React, { useMemo } from 'react';

import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { IntlProvider } from 'react-intl';
import { ApolloProvider } from '@apollo/client';
import { Layout, Modal, message } from 'antd';

import MainNavigation from 'containers/MainNavigation';
import NoMatch from 'containers/NoMatch';
import ErrorBoundary from 'components/ErrorBoundary';
import CheckForUpdates from 'components/CheckForUpdates';
import Pendo from 'components/Pendo';

import translations from 'translations';
import { GlobalContext } from 'hooks/useGlobalContext';
import useUserConfirmation from 'hooks/useUserConfirmation';
import ExtellioConfigProvider from 'ExtellioConfigProvider';
import { authenticatedClient, publicClient } from './apolloClient';
import routes from './routes';

import './app.less';

const { Header, Content } = Layout;

const locale = 'en-GB';

function PublicRoute({ children }) {
  return (
    <ApolloProvider client={publicClient}>
      <Content className="app__content">
        <ErrorBoundary>
          {children}
        </ErrorBoundary>
      </Content>
    </ApolloProvider>
  );
}

function PrivateRoute({ children }) {
  return (
    <ApolloProvider client={authenticatedClient}>
      <Header className="app__header">
        <MainNavigation />
      </Header>
      <Content className="app__content">
        <ErrorBoundary>
          {children}
        </ErrorBoundary>
      </Content>
      <Pendo />
    </ApolloProvider>
  );
}

function Router({ children }) {
  const getUserConfirmation = useUserConfirmation();

  return (
    <BrowserRouter basename={process.env.MANAGER_BASE_PATH} getUserConfirmation={getUserConfirmation}>
      {children}
    </BrowserRouter>
  );
}

function App() {
  const [modal, modalContextHolder] = Modal.useModal();
  const [msg, messageContextHolder] = message.useMessage();

  const context = useMemo(() => ({ modal, message: msg }), [modal, msg]);

  return (
    <ExtellioConfigProvider>
      <IntlProvider locale={locale} key={locale} messages={translations[locale]} textComponent="span">
        <GlobalContext.Provider value={context}>
          <Router>
            <DndProvider backend={HTML5Backend}>
              <Layout className="app">
                <ErrorBoundary>
                  <Switch>
                    {routes.map((route, index) => (
                      <Route
                        key={`main_${index}`}
                        path={route.path}
                        exact={route.exact}
                      >
                        {route.public ? <PublicRoute><route.component /></PublicRoute> : <PrivateRoute><route.component /></PrivateRoute>}
                      </Route>
                    ))}
                    <Route path="*" component={NoMatch} />
                  </Switch>
                  {process.env.NODE_ENV === 'production' && <CheckForUpdates url={`${process.env.MANAGER_BASE_PATH}/`} delay={5 * 60 * 1000} />}
                </ErrorBoundary>
              </Layout>
            </DndProvider>
          </Router>
        </GlobalContext.Provider>
        {modalContextHolder}
        {messageContextHolder}
      </IntlProvider>
    </ExtellioConfigProvider>
  );
}

export default App;
